import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { DeleteButton } from '@/features/export_files/List/Table/Columns/Cells/components/DeleteButton';
import { DownloadButton } from '@/features/export_files/List/Table/Columns/Cells/components/DownloadButton';
import { ExportFileColumn } from '@/features/export_files/List/type';
import classnames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from '../column.module.scss';

type ActionHandlers = {
  onDownloadClick: (fileId: string, fileName: string) => Promise<void>;
  onDeleteClick: (fileId: string) => Promise<void>;
};

type Props = {
  row: ExportFileColumn;
  actionHandlers?: ActionHandlers;
};

const cx = classnames.bind(styles);

const FileNameCellContent: FC<Props> = memo(
  ({
    row: { id, name, status, error_message, can_download, can_delete },
    actionHandlers,
  }) => {
    const handleDownloadClick = async () => {
      if (actionHandlers) {
        await actionHandlers.onDownloadClick(id, name);
      }
    };

    const handleDeleteClick = async () => {
      if (actionHandlers) {
        await actionHandlers.onDeleteClick(id);
      }
    };

    if (status === 'processing') return null;
    if (error_message) {
      return (
        <span
          className={cx(styles['error-message'])}
          data-testid='file-error-message'
        >
          {error_message}
        </span>
      );
    }

    return (
      <div className={cx(styles['file-name-cell'])}>
        <div className={cx(styles['file-name-container'])}>
          <FileNameSeparatorWithExtension>
            {name}
          </FileNameSeparatorWithExtension>
        </div>
        <div className={cx(styles['button-group'])}>
          {can_download && (
            <DownloadButton size='xs' onClick={handleDownloadClick} />
          )}
          {can_delete && <DeleteButton size='xs' onClick={handleDeleteClick} />}
        </div>
      </div>
    );
  }
);

FileNameCellContent.displayName = 'FileNameCellContent';

export const FileNameCell: FC<Props> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <FileNameCellContent {...props} />
    </SuspenseErrorBoundary>
  );
});

FileNameCell.displayName = 'FileNameCell';
